import { useQuery } from '@apollo/client';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@material-ui/core';
import { useUserInfo } from '@terragotech/gen5-shared-components';
import { DATETIME_TOKEN_CONVERSION, getDateTimeFormat } from '@terragotech/gen5-shared-utilities';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ROW_HEIGHT } from '../../components/Common/Modals/TGOptionLazyModal/TGOptionModalRowRenderer';
import FluidStyledTableWithData from '../../components/StyledTable/FluidStyledTableWithDataManagement';
import UnauthorizedDisplay from '../../components/UnauthorizedDisplay';
import { Filters, FilterValue } from '../../contexts/AggregatesContext/useAssetsLoader';
import { useAssetsTableState } from '../../contexts/assetsTableStateContext';
import { Column } from '../../hooks/tableHooks/useColumns';
import { TableData } from '../../hooks/useTable';
import { TableDataContext } from '../../hooks/useTableData';
import { configState } from '../../recoil/atoms';
import { colors } from '../../styles/theme';
import { GET_API_MESSAGES } from './graphql';
import { useSharedStyles } from '../LogStyles';

interface IntegrationMessage extends TableData {
  id: string;
  direction: string;
  integrationType: string;
  responseCode: number;
  requestDate: Date;
  responseDate: Date;
  relatedData: string;
  requestData: string;
  responseData: string;
  processTime: number;
}

const HEADER_HEIGHT = 109;
const QUERY = GET_API_MESSAGES;
const NUM_ROWS = 40;

/**
 * Container to get API Integrations Message data and format into table
 */
const APIMessages: React.FC = () => {
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [apiSort, setApiSort] = useState<string[]>(['NATURAL']);
  const [hasMore, setHasMore] = useState(true);
  const [messages, setMessages] = useState<IntegrationMessage[]>([]);
  const [resetComplete, setResetComplete] = useState<boolean>(false);
  const [rowsRendered, setRowsRendered] = useState<number>(NUM_ROWS);
  const { defaultDateTimeFormat } = useRecoilValue(configState);
  const sharedClasses = useSharedStyles();
  const tableHeight = window.innerHeight - HEADER_HEIGHT - 60;
  const loadingMore = useRef(false);
  const { hasAdvancedLoggingAccess } = useUserInfo();
  const { sortColumn, sortDirection } = useAssetsTableState();
  const { filters, partialFilters, setPartialFilters, setFilters, setFiltersApplied } = useContext(TableDataContext);

  useEffect(() => {
    setPartialFilters({});
    setFilters({});
    setFiltersApplied(false);
    setResetComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: Column<IntegrationMessage>[] = useMemo(
    () => [
      {
        key: 'statusIndicator',
        name: 'Status',
        width: 50,
        resizable: false,
        sortable: false,
        frozen: false,
        filterRenderer: false,
        cellRenderer: ({ row }) => (
          <div style={{ textAlign: 'center' }}>
            <FontAwesomeIcon
              icon={faCircle}
              color={row.responseCode < 200 || row.responseCode >= 300 ? colors.red : colors.success}
              size="sm"
              style={{
                fontSize: '0.75rem',
              }}
            />
          </div>
        ),
      },
      { key: 'id', name: 'ID', resizable: false, sortable: false, width: 275 },
      { key: 'direction', name: 'Direction', resizable: true, sortable: true, dataType: 'String' },
      { key: 'integrationType', name: 'Integration Type', resizable: true, sortable: true, dataType: 'String' },
      {
        key: 'responseCode',
        name: 'Response Code',
        resizable: true,
        sortable: true,
        dataType: 'String',
        cellRenderer: ({ row }) => {
          const style = row.responseCode < 200 || row.responseCode >= 300 ? { color: colors.red } : undefined;
          return <div style={style}>{row.responseCode}</div>;
        },
      },
      {
        key: 'requestDate',
        name: 'Request Date',
        dataType: 'DateTime',
        resizable: true,
        sortable: true,
        cellRenderer: ({ row }) => (
          <div>
            {row.requestDate &&
              moment(row.requestDate).format(
                getDateTimeFormat(
                  defaultDateTimeFormat?.dateFormatType,
                  defaultDateTimeFormat?.dateFormat,
                  defaultDateTimeFormat?.dateSeperator,
                  defaultDateTimeFormat?.timeFormat,
                  { tokenConversion: DATETIME_TOKEN_CONVERSION.MomentJS }
                )
              )}
          </div>
        ),
      },
      {
        key: 'responseDate',
        name: 'Response Date',
        dataType: 'DateTime',
        resizable: true,
        sortable: true,
        cellRenderer: ({ row }) => (
          <div>
            {row.responseDate &&
              moment(row.responseDate).format(
                getDateTimeFormat(
                  defaultDateTimeFormat?.dateFormatType,
                  defaultDateTimeFormat?.dateFormat,
                  defaultDateTimeFormat?.dateSeperator,
                  defaultDateTimeFormat?.timeFormat,
                  { tokenConversion: DATETIME_TOKEN_CONVERSION.MomentJS }
                )
              )}
          </div>
        ),
      },
      {
        key: 'requestData',
        name: 'Request Data',
        resizable: true,
        sortable: true,
        filterRenderer: false,
      },
      {
        key: 'responseData',
        name: 'Response Data',
        resizable: true,
        sortable: true,
        filterRenderer: false,
      },
      { key: 'processTime', name: 'Process Time (ms)', resizable: true, sortable: true },
      { key: 'relatedData', name: 'Related Data', resizable: true, sortable: true, dataType: 'String' },
    ],
    [defaultDateTimeFormat]
  );

  const { loading, fetchMore } = useQuery(QUERY, {
    variables: {
      first: NUM_ROWS,
      after: null,
      filter: { id: { isNull: false } },
      orderBy: apiSort,
    },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const edges = data?.apiMessagesConnection?.edges || [];
      const newMessages = edges.map((edge: any) => {
        const { requestData, responseData } = edge.node as { requestData: JSON; responseData: JSON };

        return {
          ...edge.node,
          requestData: requestData ? JSON.stringify(requestData) : null,
          responseData: responseData ? JSON.stringify(responseData) : null,
        };
      });

      setMessages(newMessages);
      setAfterCursor(data?.apiMessagesConnection?.pageInfo?.endCursor || null);
      setHasMore(data?.apiMessagesConnection?.pageInfo?.hasNextPage || false);
    },
    onError: () => {},
  });

  const getCurrFilter = (): Filters => {
    if (!resetComplete) return { id: { isNull: false } };
    return {
      id: { isNull: false },
      ...Object.fromEntries(
        Object.entries(filters).reduce((acc, [x, y]) => {
          const filterColumn = columns?.find(column => column.key === x);

          if (!filterColumn?.dataType || filterColumn.dataType !== 'DateTime') {
            const value: FilterValue = {
              in: Array.isArray(y) ? y : [y],
            };
            acc.push([x, value]);
          }

          return acc;
        }, [] as [string, FilterValue][])
      ),
      and: Object.entries(filters).reduce((acc, [x, y]) => {
        const filterColumn = columns?.find(column => column.key === x);

        if (filterColumn?.dataType === 'DateTime' && Array.isArray(y) && y.length >= 4) {
          acc.push({ [x]: { greaterThanOrEqualTo: y[1] } });
          acc.push({ [x]: { lessThanOrEqualTo: y[3] } });
        }

        return acc;
      }, [] as Filters[]),
      ...Object.fromEntries(
        Object.entries(partialFilters).reduce((acc, [x, y]) => {
          const filterColumn = columns?.find(column => column.key === x);

          if (filterColumn?.dataType === 'String') {
            acc.push([x, { includesInsensitive: y as string }]);
          }

          return acc;
        }, [] as [string, FilterValue][])
      ),
    };
  };

  useEffect(() => {
    if (!hasAdvancedLoggingAccess) return;
    setAfterCursor(null);
    setMessages([]);
    setRowsRendered(NUM_ROWS);
    setHasMore(true);

    const currFilter: Filters = getCurrFilter();

    const sortingColumn = columns?.find(column => column.key === sortColumn);
    if (sortingColumn?.sortable) {
      const normalizedSortColumn = sortColumn?.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
      const sortOutput =
        sortDirection === 'NONE' || !sortColumn ? 'NATURAL' : `${normalizedSortColumn}_${sortDirection}`;

      setApiSort([sortOutput, 'ID_ASC']);
    }

    fetchMore({
      variables: {
        first: NUM_ROWS,
        after: null,
        filter: currFilter,
        orderBy: apiSort,
      },
      updateQuery: (_, { fetchMoreResult }) => {
        const edges = fetchMoreResult?.apiMessagesConnection?.edges || [];
        const newMessages = edges.map((edge: any) => {
          const { requestData, responseData } = edge.node as {
            requestData: JSON;
            responseData: JSON;
          };

          return {
            ...edge.node,
            requestData: requestData ? JSON.stringify(requestData) : null,
            responseData: responseData ? JSON.stringify(responseData) : null,
          };
        });

        setMessages(newMessages);
        setAfterCursor(fetchMoreResult?.apiMessagesConnection?.pageInfo?.endCursor || null);
        setHasMore(fetchMoreResult?.apiMessagesConnection?.pageInfo?.hasNextPage || false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortColumn, sortDirection, partialFilters]);

  const loadMoreData = useCallback(
    async (input: number) => {
      if (!hasMore || loading || loadingMore.current || !hasAdvancedLoggingAccess) return;
      if (!(input + Math.floor(tableHeight / ROW_HEIGHT) >= rowsRendered - 5)) return;
      loadingMore.current = true;
      const currFilter: Filters = getCurrFilter();
      await fetchMore({
        variables: {
          first: NUM_ROWS,
          after: afterCursor,
          filter: currFilter,
          orderBy: apiSort,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const edges = fetchMoreResult?.apiMessagesConnection?.edges || [];
          const newMessages = edges.map((edge: any) => {
            const { requestData, responseData } = edge.node as { requestData: JSON; responseData: JSON };

            return {
              ...edge.node,
              requestData: requestData ? JSON.stringify(requestData) : null,
              responseData: responseData ? JSON.stringify(responseData) : null,
            };
          });

          setMessages(prev => [...prev, ...newMessages]);
          setAfterCursor(fetchMoreResult?.apiMessagesConnection?.pageInfo?.endCursor || null);
          setHasMore(fetchMoreResult?.apiMessagesConnection?.pageInfo?.hasNextPage || false);
          setRowsRendered(prevRows => prevRows + NUM_ROWS);
          loadingMore.current = false;
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasMore, fetchMore, loading, rowsRendered, loadingMore, afterCursor, filters]
  );

  return (
    <>
      {hasAdvancedLoggingAccess ? (
        <div className={sharedClasses.root}>
          {loading && !messages.length ? (
            <div className={sharedClasses.loader}>
              <CircularProgress />
            </div>
          ) : (
            <FluidStyledTableWithData<IntegrationMessage>
              columns={columns}
              data={messages}
              searchBar={() => <></>}
              searchBarHeight={0}
              onLoad={loadMoreData}
              height={tableHeight}
              disableSort
              schema={'log'}
              tableName="api_messages"
            />
          )}
        </div>
      ) : (
        <UnauthorizedDisplay />
      )}
    </>
  );
};

export default APIMessages;
